import React from 'react';

import { FastshiftRunbookStep } from '@wac-web/libraries/bxp-web-stargate-client/src/hook/use-bxp-web-stargate-client.types';
import { useIntl } from 'react-intl';

import ButtonGroup from '@atlaskit/button/button-group';
import Button from '@atlaskit/button/new';
import Modal, {
	ModalBody,
	ModalFooter,
	ModalHeader,
	ModalTitle,
	ModalTransition,
} from '@atlaskit/modal-dialog';
import { Text } from '@atlaskit/primitives';

import { messages } from '../messages';

const renderStepName = (chunks: React.ReactNode) => (
	<Text as="span" weight="bold">
		{chunks}
	</Text>
);

export type RunbookStepDisableConfirmModalProps = {
	step: FastshiftRunbookStep | null;
	isLoading: boolean;
	onClose: () => void;
	onConfirm: (step: FastshiftRunbookStep) => void;
};

export const RunbookStepDisableConfirmModal: React.FC<RunbookStepDisableConfirmModalProps> = ({
	step,
	isLoading,
	onClose,
	onConfirm,
}) => {
	const intl = useIntl();

	const isDisabling = step ? !step.disabled : false;

	return (
		<ModalTransition>
			{step && (
				<Modal onClose={onClose} testId="runbook-step-disable-confirm-modal">
					<ModalHeader>
						<ModalTitle>
							{intl.formatMessage(
								isDisabling
									? messages.runbooksPanelDisableModalTitle
									: messages.runbooksPanelEnableModalTitle,
							)}
						</ModalTitle>
					</ModalHeader>
					<ModalBody>
						<Text>
							{intl.formatMessage(
								isDisabling
									? messages.runbooksPanelDisableModalBody
									: messages.runbooksPanelEnableModalBody,
								{ title: step.title, stepName: renderStepName },
							)}
						</Text>
					</ModalBody>
					<ModalFooter>
						<ButtonGroup>
							<Button appearance="subtle" onClick={onClose} isDisabled={isLoading}>
								{intl.formatMessage(messages.runbooksPanelModalCancelButton)}
							</Button>
							<Button
								appearance={isDisabling ? 'danger' : 'primary'}
								isLoading={isLoading}
								onClick={() => onConfirm(step)}
								testId="runbook-step-disable-confirm-button"
							>
								{intl.formatMessage(
									isDisabling
										? messages.runbooksPanelDisableModalConfirmButton
										: messages.runbooksPanelEnableModalConfirmButton,
								)}
							</Button>
						</ButtonGroup>
					</ModalFooter>
				</Modal>
			)}
		</ModalTransition>
	);
};
